import { Component, OnInit, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { PageEvent } from '@angular/material/paginator';
import { HttpParameter } from 'src/app/types/http-parameter';
import { Sort } from '@angular/material/sort';
import { PrintService } from 'src/app/services/print.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { FormBuilder, NgForm } from '@angular/forms';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { Renderer2 } from '@angular/core';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '../../../services/currency.service';
import { LoggerService } from '../../../services/logger.service';

// Interface for grouped data structure
interface GroupedPayment {
  type: 'payment' | 'subtotal' | 'total';
  date?: string;
  data?: any;
  subtotalAmount?: number;
  totalAmount?: number;
}

@Component({
  selector: 'app-payments-made-encompassing',
  templateUrl: './payments-made-encompassing.component.html',
  styleUrls: ['./payments-made-encompassing.component.css']
})
export class PaymentsMadeEncompassingComponent extends ViewTableComponent implements OnInit {

  currentPage: number;
  groupedDataSource: MatTableDataSource<GroupedPayment>;
  rawData: any[] = [];
  originalDataLength: number = 0;
  private localSubscription: any;

  // When an instructor filter is active, surface commission columns and
  // recompute the day subtotals / grand total against the per-row commission
  // share instead of gross tuition.
  selectedInstructorId: string | null = null;
  selectedInstructorName: string | null = null;

  private readonly baseDisplayedColumns: string[] = [
    'id',
    'field_date_and_time',
    'field_student_name',
    'field_student_department',
    'field_enrollment_package_name_label',
    'field_gross_tuition',
    'field_payment_type_label',
    'field_instructor_label',
    'closer',
  ];

  constructor(
    private _printService: PrintService,
    public override _refreshService: RefreshService,
    public override _dialogService: DialogService,
    public override _entityRESTService: EntityRESTService,
    public override _utilityService: UtilityService,
    public override _fieldsService: FieldsService,
    public override _drupalRESTService: DrupalRESTService,
    public override _taxonomyService: TaxonomyService,
    public override _router: Router,
    public override _activatedRoute: ActivatedRoute,
    public override _authRESTService: AuthRESTService,
    public override _authService: AuthService,
    public override _menuRESTService: MenuRESTService,
    public override _formBuilder: FormBuilder,
    public override _routeEventsService: RouteEventsService,
    public override _permissionsService: PermissionsService,
    public override _renderer2: Renderer2,
    public override _configPages: ConfigPagesService,
    public override _packagesService: PackagesService,
    public override _snackBar: MatSnackBar,
    public override _userRESTService: UserService,
    public override _el: ElementRef,
    public override _changeDetectorRef: ChangeDetectorRef,
    public override _sanitizer: DomSanitizer,
    public override _studioConfigService: StudioConfigService,
    public override _componentRefreshService: ComponentRefreshService,
    public override _translateService: TranslateService,
    private currencyService: CurrencyService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  /**
   * The default window: Monday of the studio's current week through the
   * studio's today (week to date). Monday-start ISO weeks, matching the AMI
   * weekly reports (Misc, Royalty).
   *
   * Without a date filter the view returns every payment ever recorded, and the
   * server-side pager then showed only the first page of it. Both ends are
   * resolved in the studio's timezone, never the browser's, so a studio east or
   * west of the viewer doesn't get a window off by a day, or on a Monday, off
   * by a whole week.
   */
  private defaultRangeStart(): Date {
    return moment(this.reportDateService.currentStudioDateKey(), 'YYYY-MM-DD', true)
      .startOf('isoWeek').toDate();
  }

  private defaultRangeEnd(): Date {
    return this.reportDateService.studioTodayStart();
  }

  /**
   * Total rows the current filters matched, as reported by the backend. Bound
   * to the paginator's length so the range label reflects the whole result set
   * rather than the hardcoded placeholder it used to show.
   */
  totalRowCount = 0;

  /**
   * Amount Paid (or the filtered instructor's share) summed by the backend over
   * every matched payment, so the TOTAL row is the report total on every page
   * rather than a total of the rows on screen.
   */
  private reportTotal?: number | string;

  dateRange: Date[] = [];

  /** Rows fetched per page when the report opens or is cleared. */
  private static readonly DEFAULT_PAGE_SIZE = 500;

  /**
   * Largest page: the show-everything option and the print fetch.
   *
   * Bounded rather than unlimited on purpose. items_per_page=0 (the backend's
   * no-range escape hatch, used by the CSV export) exhausts PHP mid-response on
   * a wide range and resolves as a 200 with an empty body.
   */
  private static readonly MAX_PAGE_SIZE = 5000;

  override displayedColumns: string[] = [...this.baseDisplayedColumns];

  private rebuildDisplayedColumns(): void {
    const cols = [...this.baseDisplayedColumns];
    if (this.selectedInstructorId) {
      // Insert commission columns right after Amount Paid for natural reading.
      const idx = cols.indexOf('field_gross_tuition');
      cols.splice(idx + 1, 0, 'instructor_percentage', 'commission_amount');
    }
    this.displayedColumns = cols;
  }

  override ngOnInit() {
    // Initialize pagination values
    this.paginationIndex = 0;
    this.pageSize = PaymentsMadeEncompassingComponent.DEFAULT_PAGE_SIZE;

        // Custom subscription to avoid double getData calls
    this.localSubscription = this._activatedRoute.queryParams
      .subscribe((routeParams: any) => {
        if (routeParams && Object.keys(routeParams).length > 0) {
          // Convert route params to queryParams format
          this.queryParams = Object.entries(routeParams)
            .filter(([key]) => key !== 'timestamp')
            .map(([parameter, value]) => ({ parameter, value: value as string }));
        } else {
          this.queryParams = [];
        }
        // The URL is the source of truth for the active filters. pageChanged()
        // and dateRangeChange() rebuild the next URL from routeParams, and the
        // router replaces the query string wholesale (queryParamsHandling: ''),
        // so a routeParams that lagged the URL dropped start_date/end_date on
        // every page change and the picker fell back to week to date.
        this.syncRouteStateFromUrl(routeParams);
        // Sync the picker with the route: a bookmarked link shows the range it
        // names, and a bare /payments-made opens on week to date. Must run
        // after queryParams is populated, since that is what it inspects.
        this.syncDateRangeWithRoute();
        // Load the data with the updated params
        this.getData(this.queryParams);
      });
  }

  /**
   * Mirror the current URL into routeParams (minus the cache-busting
   * timestamp) and the pager state, so the next navigation built from
   * routeParams carries every filter already applied, the date range included,
   * and a bookmarked page/page size shows in the paginator.
   */
  private syncRouteStateFromUrl(urlParams: any): void {
    const { timestamp, ...rest } = urlParams ?? {};
    this.routeParams = { ...rest };

    const page = parseInt(rest.page, 10);
    this.paginationIndex = Number.isFinite(page) && page >= 0 ? page : 0;

    const perPage = parseInt(rest.items_per_page, 10);
    this.pageSize = Number.isFinite(perPage) && perPage > 0
      ? perPage
      : PaymentsMadeEncompassingComponent.DEFAULT_PAGE_SIZE;
  }

  /**
   * Seed the picker with week to date: this Monday through today. The owl picker
   * binds to dateRange rather than the form, so this is a plain assignment.
   */
  private applyDefaultDateRange(): void {
    this.dateRange = [this.defaultRangeStart(), this.defaultRangeEnd()];
  }

  /**
   * Mirror the route's range into the picker, falling back to the week-to-date
   * default when the route carries none.
   */
  private syncDateRangeWithRoute(): void {
    const routeValue = (name: string) =>
      this.queryParams?.find(p => p.parameter === name)?.value;

    const start = routeValue('start_date');
    const end = routeValue('end_date');

    if (start && end) {
      const parsedStart = this.reportDateService.parseCalendarDateInput(start);
      const parsedEnd = this.reportDateService.parseCalendarDateInput(end);
      if (parsedStart && parsedEnd) {
        this.dateRange = [parsedStart, parsedEnd];
        return;
      }
    }

    this.applyDefaultDateRange();
  }

  /**
   * Push the owl-picked range through the router as start_date/end_date, which
   * buildRequestParams() converts into the field_date_and_time contextual
   * param. Routed (rather than straight to getData) so the range stays in the
   * URL and the report link remains copyable.
   */
  dateRangeChange(event: any): void {
    if (!event?.value || !Array.isArray(event.value)) {
      return;
    }
    this.dateRange = event.value;
    if (!this.dateRange[0] || !this.dateRange[1]) {
      return;
    }
    // A new range starts back on the first page; the page size is kept.
    const { page, ...current } = this.routeParams ?? {};
    this.updateRouterParams({
      ...current,
      start_date: moment(this.dateRange[0]).format('YYYY-MM-DD'),
      end_date: moment(this.dateRange[1]).format('YYYY-MM-DD'),
    });
  }

  override ngOnDestroy(): void {
    if (this.localSubscription) {
      this.localSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }

  override onSubmit(form: NgForm): void {
    // fix for form submitting on (blur) and (input)
    if (this.ShowProgressBar) { return; }

    LoggerService.log('submitting form values', form.form.value)

    // Convert date value(s) to what Drupal expects. YYYY/MM/DD
    for (const property in form.form.value) {
      if (moment.isMoment(form.form.value[property]) == true) {
        const momentValue = form.form.value[property] as moment.Moment;
        form.form.value[property] = momentValue ? this.reportDateService.formatCalendarParam(momentValue.toDate()) : '';
      }
    }

    // Build query params - INCLUDING unchecked checkboxes for proper filtering
    this.queryParams = [];
    for (const property in form.form.value) {
      // Include all form values, even false/unchecked checkboxes
      if (form.form.value[property] !== null && form.form.value[property] !== undefined && form.form.value[property] !== "") {
        this.queryParams.push({ parameter: property, value: form.form.value[property] })
      }
    }

    // Build the route params - only include truthy values for URL. Keep the
    // chosen page size; applying a filter starts back on the first page.
    const keptPageSize = (this.routeParams as any)?.['items_per_page'];
    this.routeParams = keptPageSize ? { items_per_page: keptPageSize } : new Object;
    for (const property in form.form.value) {
      if (form.form.value[property] && form.form.value[property] != "" && form.form.value[property] !== false) {
        this.routeParams = {
          ...this.routeParams,
          [property]: form.form.value[property]
        }
      }
    }

    // The picker binds to the form as a raw [start, end] Date array, which the
    // URL cannot round-trip: syncDateRangeWithRoute() reads start_date/end_date
    // and would fall back to the week-to-date default, silently resetting the
    // range whenever another filter was applied. Emit the same pair
    // dateRangeChange() does so the picked range survives every other filter.
    const submittedRange = (this.routeParams as any)?.['field_date_and_time'];
    delete (this.routeParams as any)?.['field_date_and_time'];
    const rangeStart = Array.isArray(submittedRange) ? submittedRange[0] : this.dateRange?.[0];
    const rangeEnd = Array.isArray(submittedRange) ? submittedRange[1] : this.dateRange?.[1];
    if (rangeStart && rangeEnd) {
      this.routeParams = {
        ...this.routeParams,
        start_date: moment(rangeStart).format('YYYY-MM-DD'),
        end_date: moment(rangeEnd).format('YYYY-MM-DD'),
      };
    }

    // Update the query parameters, so the link can be copied.
    // Force a complete replacement of query params to avoid merging old unchecked values
    this.updateRouterParams(this.routeParams);
  }

  private isUpdatingParamsLocal = false;
  // Holds the most recent params requested while a navigation is in flight, so a
  // rapid second call (e.g. a second date range picked before the first one has
  // settled) is replayed instead of silently dropped. `undefined` = nothing
  // pending; a value (incl. {} for "clear") = replay it when the navigation ends.
  private pendingRouteParamsLocal: any = undefined;

  override updateRouterParams(routeParams?: any) {
    if (this.isUpdatingParamsLocal) {
      // Don't discard. Remember the latest request and replay it when the
      // in-flight navigation finishes (see onComplete below).
      this.pendingRouteParamsLocal = routeParams ?? {};
      return;
    }

    this.isUpdatingParamsLocal = true;

    const onComplete = () => {
      this.isUpdatingParamsLocal = false;
      // Replay the latest queued request, if any.
      if (this.pendingRouteParamsLocal !== undefined) {
        const next = this.pendingRouteParamsLocal;
        this.pendingRouteParamsLocal = undefined;
        this.updateRouterParams(next);
      }
    };

    if (routeParams && Object.keys(routeParams).length > 0) {
      if (!routeParams.hasOwnProperty('page')) {
        LoggerService.log('Adding default page parameter');
        routeParams = {
          ...routeParams,
          page: '0',
        };
      }

      // Use '' instead of 'merge' to completely replace query params: onSubmit
      // drops unchecked boxes from routeParams, so merging would keep the
      // previous URL's value and leave a cleared filter effectively set.
      this._router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: routeParams,
        queryParamsHandling: ''  // Complete replacement instead of merge
      }).finally(onComplete);

      // Convert route params to queryParams
      this.queryParams = Object.entries(routeParams)
        .filter(([key]) => key !== 'timestamp')
        .map(([parameter, value]) => ({ parameter, value: value as string }));
    } else {
      LoggerService.log('Clearing all query params');
      this._router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: {},
        queryParamsHandling: ''  // This will clear all existing query params
      }).finally(onComplete);

      this.queryParams = [];
    }
  }

    override getData(params: { parameter: string; value: string; }[]) {
    this.ShowProgressBar = true;

    // Store only non-pagination params in queryParams
    this.queryParams = params.filter(param =>
      param.parameter !== 'page' && param.parameter !== 'items_per_page'
    );

    // Add current pagination state to params if not present. Checked
    // independently: updateRouterParams() injects page=0 into every navigation
    // that lacks it, so a combined guard left items_per_page off the request
    // and the backend fell back to its own default of 10 rows while still
    // reporting the full count, showing "1 - 155 of 155" above ten rows.
    let finalParams = [...params];
    if (!params.some(p => p.parameter === 'page')) {
      finalParams.push({ parameter: 'page', value: this.paginationIndex.toString() });
    }
    if (!params.some(p => p.parameter === 'items_per_page')) {
      finalParams.push({ parameter: 'items_per_page', value: this.pageSize.toString() });
    }

    finalParams = this.buildRequestParams(finalParams);

    // Load the new data with the filtering parameters.
    let endpoint: string = "/api/v1/payments-made";

    this._drupalRESTService.httpGET(endpoint, finalParams)
      .subscribe((data) => {
        // ShowProgressBar
        this.ShowProgressBar = false;

        // Store raw data
        this.rawData = data['results'] || [];
        this.originalDataLength = this.rawData.length;

        // Group data and create subtotals
        // The grand total covers every matched payment, not just this page.
        this.reportTotal = data['grand_total'];
        const groupedData = this.groupDataByDate(this.rawData, this.reportTotal);
        this.groupedDataSource = new MatTableDataSource(groupedData);

        // Keep original dataSource for compatibility
        this.dataSource = new MatTableDataSource(data['results']);

        // Pagination
        if (data['results']) {
          this.setupPagination(data, params);
        }
      },
        error => this.handleError(error)
      )
  }

  /**
   * Translate the raw form/route params into the query the backend expects
   * (date range, SPS-code checkboxes, sorting) and sync the instructor-driven
   * commission columns. Shared by getData() and onPrint() so the printed
   * report is guaranteed to reflect the same filters as the on-screen table.
   */
  private buildRequestParams(params: { parameter: string; value: string; }[]) {
    let finalParams = [...params];

    // Add sorting parameters if available
    if (this.sortParams !== undefined) {
      finalParams.push(this.sortParams);
    }

    // Alter start_date and end_date params for Drupal.
    let start_date;
    let end_date;
    let field_next_scheduled_payment;

    if (finalParams) {
      for (const property in finalParams) {
        if (finalParams[property]?.['parameter'] == 'start_date') {
          start_date = this.reportDateService.formatCalendarParam(finalParams[property]?.['value']);
        }
        if (finalParams[property]?.['parameter'] == 'end_date') {
          end_date = this.reportDateService.formatCalendarParam(finalParams[property]?.['value']);
        }
      }

      // The owl picker lives outside the form, so paging, sorting and print
      // rebuild their params without it. Fall back to whatever the picker is
      // currently showing so every request stays inside the visible window.
      if (!start_date && this.dateRange?.[0]) {
        start_date = this.reportDateService.formatCalendarParam(this.dateRange[0]);
      }
      if (!end_date && this.dateRange?.[1]) {
        end_date = this.reportDateService.formatCalendarParam(this.dateRange[1]);
      }

      field_next_scheduled_payment = {
        parameter: 'field_date_and_time',
        value: start_date + "--" + end_date,
      }
    }

    if (field_next_scheduled_payment && field_next_scheduled_payment?.['value'] != 'undefined--undefined') {
      finalParams = [
        ...finalParams,
        field_next_scheduled_payment
      ];
    }


    // Handle different checkbox based filters.
    let field_sps_code;
    let sps_codes_array: string[] = [];

    // Check for each checkbox type and only add if the parameter exists with a truthy value
    const sundryParam = finalParams.find(p => p.parameter === 'Sundry');
    const miscParam = finalParams.find(p => p.parameter === 'Misc');
    const lessonParam = finalParams.find(p => p.parameter === 'Lesson');

    if (sundryParam && sundryParam.value && sundryParam.value !== 'false') {
      sps_codes_array.push("558");
    }
    if (miscParam && miscParam.value && miscParam.value !== 'false') {
      sps_codes_array.push("554");
    }
    if (lessonParam && lessonParam.value && lessonParam.value !== 'false') {
      sps_codes_array.push("1394", "550", "549", "547", "552");
    }

    // Join the codes with + if any were selected
    const sps_codes = sps_codes_array.join('+');

    LoggerService.log('Checkbox states - Sundry:', sundryParam?.value, 'Misc:', miscParam?.value, 'Lesson:', lessonParam?.value);
    LoggerService.log('Final sps_codes:', sps_codes);

    if (sps_codes) {
      field_sps_code = {
        parameter: 'field_sps_code',
        value: sps_codes,
      }
    }

    if (field_sps_code && field_sps_code?.['value'] != '') {
      finalParams = [
        ...finalParams,
        field_sps_code
      ];
    }

    // Reflect the instructor filter into commission-column visibility before
    // grouping, so subtotals/grand total sum the right column.
    const instructorParam = finalParams.find(p => p.parameter === 'field_instructor_target_id');
    this.selectedInstructorId = instructorParam?.value ? String(instructorParam.value) : null;
    this.selectedInstructorName = this.selectedInstructorId
      ? ((this._fieldsService.instructorData() ?? []).find((i: any) => String(i.uid) === this.selectedInstructorId)?.value ?? null)
      : null;
    this.rebuildDisplayedColumns();

    return finalParams;
  }

  override resetForm() {
    // Clear all stored params
    this.queryParams = [];
    this.sortParams = undefined;

    // Reset pagination
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginationIndex = 0;  // Reset the pagination index
      this.pageSize = PaymentsMadeEncompassingComponent.DEFAULT_PAGE_SIZE;
    }

    // Clear the form, then put the default window back so Clear returns to the
    // same view the report opens with rather than an unbounded one.
    this.form.resetForm();
    this.applyDefaultDateRange();

    // Clear any autocomplete options
    this.autoCompleteEnrollmentOptions = [];
    this.autoCompletePackageOptions = [];

    // Clear data sources
    this.rawData = [];
    this.groupedDataSource = new MatTableDataSource([]);

    // Make a fresh request with explicitly empty params
    this.getData([
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: PaymentsMadeEncompassingComponent.DEFAULT_PAGE_SIZE.toString() }
    ]);

    // Update router last
    this.updateRouterParams({});
  }

  override pageChanged(event: PageEvent) {
    LoggerService.log('Custom pageChanged called...');

    // Update pagination state
    this.paginationIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    // Build pagination params for router
    let paginationParams = {
      page: event.pageIndex.toString(),
      items_per_page: event.pageSize.toString(),
      timestamp: new Date().getTime().toString()
    };

    // Carry the range the picker shows into the URL even when it is the
    // week-to-date default (a bare /payments-made has no start_date/end_date),
    // so the next page is fetched for the same window and the link stays
    // copyable.
    const rangeParams: any = {};
    if (this.dateRange?.[0] && this.dateRange?.[1]) {
      rangeParams.start_date = moment(this.dateRange[0]).format('YYYY-MM-DD');
      rangeParams.end_date = moment(this.dateRange[1]).format('YYYY-MM-DD');
    }

    // Use our custom updateRouterParams to trigger single getData call
    this.updateRouterParams({ ...this.routeParams, ...rangeParams, ...paginationParams });
  }

    override setupPagination(data: any, params: { parameter: string; value: string; }[]) {
    const pageParam = params.find(param => param.parameter === 'page');

    // Check if paginator exists before accessing its properties
    this.totalRowCount = Number(data?.['count'] ?? 0);

    if (this.paginator) {
      this.paginator.length = this.totalRowCount;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageIndex = pageParam ? parseInt(pageParam.value, 10) : 0;
    }

    // DO NOT call updateRouterParams here - this prevents duplicate requests
    // The routing has already been handled by pageChanged() or other methods
    LoggerService.log('setupPagination called - skipping route update to prevent duplicates');
  }

  override onPageSizeOptions() {
    // The last option is the show-everything page (see MAX_PAGE_SIZE).
    return [10, 25, 50, 100, 250, 500, PaymentsMadeEncompassingComponent.MAX_PAGE_SIZE];
  }

  /**
   * Groups payment data by date and adds subtotal rows
   */
  private groupDataByDate(data: any[], reportTotal?: number | string): GroupedPayment[] {
    if (!data || data.length === 0) {
      return [];
    }

    LoggerService.log('Raw data sample:', data[0]); // Debug log to see data structure

    // Group data by date
    const grouped = data.reduce((groups, payment, index) => {
      let date = 'unknown-date';

      const possibleDateFields = ['field_date_and_time', 'date', 'created', 'field_date'];

      for (const field of possibleDateFields) {
        if (payment[field]) {
          date = this.reportDateService.formatDateKey(payment[field], 'unknown-date');
          if (date !== 'unknown-date') {
            break;
          }
        }
      }

      LoggerService.log('Final parsed date for payment:', date);

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push({ ...payment, originalIndex: index });
      return groups;
    }, {});

    LoggerService.log('Grouped by date:', grouped);

    // Convert to flat array with subtotals
    const result: GroupedPayment[] = [];

    // Get all unique dates and sort them in descending order (newest first)
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (a === 'unknown-date') return 1;
      if (b === 'unknown-date') return -1;
      return b.localeCompare(a);
    });

    LoggerService.log('Sorted dates:', sortedDates);

    let grandTotal = 0;

    sortedDates.forEach(date => {
      const payments = grouped[date];

      // Sort payments within the day by original index to maintain order
      payments.sort((a, b) => a.originalIndex - b.originalIndex);

      // Add payment rows for this date
      payments.forEach(payment => {
        result.push({
          type: 'payment',
          date: date,
          data: payment
        });
      });

      // Calculate subtotal for this date. When filtered to a single instructor,
      // sum the per-row commission share so the bottom-of-page total reflects
      // what to actually pay that instructor.
      const amountField = this.selectedInstructorId ? 'commission_amount' : 'field_gross_tuition';
      const subtotal = payments.reduce((sum, payment) => {
        return sum + (parseFloat(payment[amountField]) || 0);
      }, 0);

      grandTotal += subtotal;

      // Add subtotal row immediately after the payments for this date
      result.push({
        type: 'subtotal',
        date: date,
        subtotalAmount: subtotal
      });
    });

    // Add grand total row at the end
    if (sortedDates.length > 0) {
      result.push({
        type: 'total',
        totalAmount: reportTotal != null ? Number(reportTotal) : grandTotal
      });
    }

    LoggerService.log('Final grouped data result:', result);
    return result;
  }

  /**
   * Check if row is a subtotal row
   */
  isSubtotalRow(row: GroupedPayment): boolean {
    return row.type === 'subtotal';
  }

  /**
   * Check if row is a payment row
   */
  isPaymentRow(row: GroupedPayment): boolean {
    return row.type === 'payment';
  }

  /**
   * Check if row is a total row
   */
  isTotalRow(row: GroupedPayment): boolean {
    return row.type === 'total';
  }

  /**
   * Override sortData to handle grouped data
   */
  override sortData(sort: Sort) {
    // Call parent method to handle sort params
    super.sortData(sort);

    // Apply sorting to raw data before regrouping
    if (sort.active && sort.direction !== '') {
      this.rawData = this.rawData.sort((a, b) => {
        const aValue = a[sort.active];
        const bValue = b[sort.active];

        // Handle different data types
        let comparison = 0;
        if (aValue && bValue) {
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            comparison = aValue.localeCompare(bValue);
          } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            comparison = aValue - bValue;
          } else {
            comparison = String(aValue).localeCompare(String(bValue));
          }
        }

        return sort.direction === 'asc' ? comparison : -comparison;
      });

      // Re-group the sorted data
      const groupedData = this.groupDataByDate(this.rawData, this.reportTotal);
      this.groupedDataSource.data = groupedData;
    }
  }

  /**
   * Custom print method for grouped payments report.
   *
   * The on-screen table is server-paged, so printing from groupedDataSource
   * would emit only the current page and the "TOTAL" row would be a page total
   * wearing a report total's label.
   *
   * Re-fetches at MAX_PAGE_SIZE rather than reusing groupedDataSource, so
   * the printout still covers the full range if the user has narrowed the
   * on-screen table to a smaller page.
   */
  override onPrint() {
    // Must happen here, in the click handler: the fetch below ends the user
    // gesture, so opening the window from its callback trips the popup blocker.
    const printWindow = this._printService.openPrintWindow();
    if (!printWindow) {
      this._snackBar.open(
        'Your browser blocked the print window. Allow pop-ups for this site and try again.',
        'Close', { duration: 8000 });
      return;
    }

    this.ShowProgressBar = true;

    const limit = PaymentsMadeEncompassingComponent.MAX_PAGE_SIZE;
    const params = this.buildRequestParams([
      ...this.queryParams,
      { parameter: 'page', value: '0' },
      { parameter: 'items_per_page', value: limit.toString() },
    ]);

    this._drupalRESTService.httpGET('/api/v1/payments-made', params)
      .subscribe((data) => {
        this.ShowProgressBar = false;

        // A dead PHP worker surfaces as a 200 with an empty body, which would
        // otherwise be indistinguishable from a genuinely empty result set.
        if (!data || data['results'] === undefined) {
          printWindow.close();
          this._snackBar.open(
            'Could not build the printout. The report did not return. Try a narrower date range.',
            'Close', { duration: 8000 });
          return;
        }

        const rows = data['results'] || [];
        if (!rows.length) {
          printWindow.close();
          this._snackBar.open('No payments found for the selected criteria.', 'Close', { duration: 5000 });
          return;
        }

        const total = Number(data['count'] ?? rows.length);
        if (total > rows.length) {
          this._snackBar.open(
            `This range matches ${total} payments; printing the first ${rows.length}. Narrow the date range to print the rest.`,
            'Close', { duration: 8000 });
        }

        this.renderPrintout(this.groupDataByDate(rows), printWindow);
      },
        error => {
          this.ShowProgressBar = false;
          printWindow.close();
          this.handleError(error);
        }
      );
  }

  /**
   * Build and hand off the printable HTML for an already-grouped result set.
   */
  private renderPrintout(groupedData: GroupedPayment[], printWindow: Window | null) {
    const withCommission = !!this.selectedInstructorId;
    const instructorName = this.selectedInstructorName ?? 'this instructor';
    const commissionHeaders = withCommission
      ? `<th>Allotted %</th><th>Reported $ &mdash; ${this.escapeHtml(instructorName)}</th>`
      : '';

    // Build the HTML content for printing
    let content = `
      <div class="report-container">
        <h1 class="report-title">Payments Made Report</h1>

        <div class="report-section">
          <table class="report-table">
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Date Paid</th>
                <th>Student Name</th>
                <th>Student Department</th>
                <th>Enrollment</th>
                <th>Amount Paid</th>
                ${commissionHeaders}
                <th>Payment Type</th>
                <th>Instructor</th>
                <th>Closer</th>
              </tr>
            </thead>
            <tbody>`;

    // Add rows from grouped data. Callers guarantee a non-empty set.
    groupedData.forEach((item: GroupedPayment) => {
        if (item.type === 'payment') {
          const payment = item.data;
          const formattedDate = this.formatDate(payment.field_date_and_time);
          const commissionFormulaText = withCommission ? this.commissionFormula(payment) : '';
          const commissionCells = withCommission
            ? `<td>${this.formatPercent(payment.instructor_percentage)}%</td>
               <td>${this.currencyService.format(payment.commission_amount)} <span class="commission-formula">${this.escapeHtml(commissionFormulaText)}</span></td>`
            : '';
          content += `
            <tr class="payment-row">
              <td>${this.escapeHtml(payment.id)}</td>
              <td>${formattedDate}</td>
              <td>${this.escapeHtml(payment.field_student_name)}</td>
              <td>${this.escapeHtml(payment.field_student_department)}</td>
              <td>${this.escapeHtml(payment.enrollment_title)}</td>
              <td>${this.currencyService.format(payment.field_gross_tuition)}</td>
              ${commissionCells}
              <td>${this.escapeHtml(payment.field_payment_type_label)}</td>
              <td>${this.escapeHtml(payment.field_first_name_instructor)} ${this.escapeHtml(payment.field_last_name_instructor)}</td>
              <td>${this.escapeHtml(payment.field_first_name_executive)} ${this.escapeHtml(payment.field_last_name_executive)}</td>
            </tr>`;
        } else if (item.type === 'subtotal') {
          // When filtered: keep the Amount Paid column blank and show the
          // (commission-summed) total under the new Reported $ column instead.
          const amountCell = withCommission ? `<td></td>` : `<td><strong>${this.currencyService.format(item.subtotalAmount)}</strong></td>`;
          const commissionTotalCells = withCommission
            ? `<td></td><td><strong>${this.currencyService.format(item.subtotalAmount)}</strong></td>`
            : '';
          content += `
            <tr class="subtotal-row">
              <td></td>
              <td></td>
              <td><strong>Subtotal for ${this.formatDate(item.date)}</strong></td>
              <td></td>
              <td></td>
              ${amountCell}
              ${commissionTotalCells}
              <td></td>
              <td></td>
              <td></td>
            </tr>`;
        } else if (item.type === 'total') {
          const totalLabel = withCommission ? `Total to ${this.escapeHtml(instructorName)}: ` : '';
          const amountCell = withCommission ? `<td></td>` : `<td><strong>${this.currencyService.format(item.totalAmount)}</strong></td>`;
          const commissionTotalCells = withCommission
            ? `<td></td><td><strong>${totalLabel}${this.currencyService.format(item.totalAmount)}</strong></td>`
            : '';
          content += `
            <tr class="total-row">
              <td></td>
              <td></td>
              <td><strong>TOTAL</strong></td>
              <td></td>
              <td></td>
              ${amountCell}
              ${commissionTotalCells}
              <td></td>
              <td></td>
              <td></td>
            </tr>`;
        }
    });

    content += `
            </tbody>
          </table>
        </div>
      </div>`;

    // Define custom styles for the report
    const styles = `
      .report-container {
        font-family: Arial, sans-serif;
        margin: 0 auto;
        padding: 20px;
        max-width: 100%;
      }
      .report-title {
        font-size: 24px;
        text-align: center;
        margin-bottom: 20px;
        color: #333;
      }
      .commission-formula {
        color: #777;
        font-size: 10px;
        margin-left: 4px;
      }
      .report-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      .report-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
        font-size: 12px;
      }
      .report-table th, .report-table td {
        border: 1px solid #ddd;
        padding: 6px;
        text-align: left;
      }
      .report-table th {
        background-color: #f2f2f2;
        font-weight: bold;
        font-size: 11px;
      }
      .payment-row:nth-child(even) {
        background-color: #f9f9f9;
      }
      .subtotal-row {
        background-color: #e6e6e6 !important;
        border-top: 2px solid #333;
        border-bottom: 2px solid #333;
      }
      .subtotal-row td {
        font-weight: bold;
      }
      .total-row {
        background-color: #d0d0d0 !important;
        border-top: 3px double #000;
        border-bottom: 3px double #000;
      }
      .total-row td {
        font-weight: bold;
        font-size: 13px;
      }
      .no-data {
        text-align: center;
        font-style: italic;
        color: #999;
      }
      @media print {
        .report-section {
          page-break-inside: avoid;
        }
        .subtotal-row {
          page-break-inside: avoid;
        }
        .total-row {
          page-break-inside: avoid;
        }
      }
    `;

    // Use the PrintService to print the report
    this._printService.print(content, {
      title: 'Payments Made Report',
      styles: styles,
      showSchoolName: true,
      promptPrint: true
    }, printWindow);
  }

  /**
   * Format currency for display
   */
  private formatCurrency(value: number | string | null | undefined): string {
    return this._utilityService.formatCurrency(value);
  }

  /**
   * Handle package name autocomplete using dayview autocomplete endpoint
   */
  onPackageAutocomplete($event: { target: { value: string; }; }) {
    let term: string = $event.target.value;
    let eckType: string = 'packages';
    let bundle: string = 'package';
    let field: string = 'field_package_name';

    this.onAutocomplete(term, eckType, bundle, field)
      .subscribe((data: any) => {
        this.autoCompletePackageOptions = data;
      });
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null | undefined): string {
    return this.reportDateService.formatDate(dateString, 'N/A');
  }

  /**
   * Build the per-row commission decomposition shown next to the dollar value,
   * e.g. "(50% × $75.00)". Source-of-truth is the data already on the row
   * (instructor_percentage and field_gross_tuition) so it can never disagree
   * with commission_amount.
   */
  commissionFormula(payment: any): string {
    const pct = payment?.instructor_percentage;
    const gross = payment?.field_gross_tuition;
    if (pct === undefined || pct === null || gross === undefined || gross === null) {
      return '';
    }
    // When this instructor gets the whole payment (the 100% fallback case for
    // legacy enrollments without per-instructor splits), the formula is just
    // "$X.XX (100% × $X.XX)": redundant noise next to the bare dollar value,
    // so suppress it. Co-instructor splits still get the decomposition.
    if (parseFloat(pct as any) === 100) {
      return '';
    }
    return `(${this.formatPercent(pct)}% × ${this.currencyService.format(gross)})`;
  }

  /**
   * Trim trailing zero-decimals from a percentage string/number so "50.00"
   * renders as "50" and "33.33" stays as "33.33". Mirrors the digit-info
   * '1.0-2' pattern used in other templates in this app.
   */
  formatPercent(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') { return '0'; }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) { return '0'; }
    return num.toString();
  }

  private escapeHtml(value: string | null | undefined): string {
    if (value === null || value === undefined) { return ''; }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
